import { initializeApp, getApps, getApp } from "firebase/app"
import { getMessaging, getToken, deleteToken, onMessage } from "firebase/messaging"

import * as configurationApi from "@/api/configurationApi.js"
import * as environment from "@/services/environment.js"

import * as deviceApi from "@/api/deviceApi.js"

import { useConfigurationStore } from "@/store/configuration.js"
import { useAppStateStore } from "@/store/appState.js"

let app
let messaging

export const initializePushNotifications = async () => {
  const configurationStore = useConfigurationStore()
  const appStateStore = useAppStateStore()

  const firebaseConfig = configurationStore.configuration.notifications?.firebase_config
  if (!environment.supportsPushNotifications() || !firebaseConfig || !appStateStore.enablePushNotifications || Notification.permission !== "granted") {
    return
  }

  if (getApps().length) {
    app = getApp()
  } else {
    app = initializeApp(firebaseConfig)
  }

  messaging = getMessaging(app)

  await refreshPushNotificationMessagingToken()

  const pnChannel = new BroadcastChannel("serviceWorkerPushNotifications")
  onMessage(messaging, async (event) => {
    //Ignore data messages
    if (!event.notification) {
      return
    }

    await navigator.serviceWorker.ready

    pnChannel.postMessage(event)
  })
}

const pushNotificationPermissionInstructionUrl = {
  chromeAndroid: "https://support.google.com/chrome/answer/3220216?co=GENIE.Platform%3DAndroid&hl=en",
  chromeDesktop: "https://support.google.com/chrome/answer/3220216?co=GENIE.Platform%3DDesktop&hl=en",
  firefox: "https://support.mozilla.org/en-US/kb/push-notifications-firefox#w_upgraded-notifications",
  opera: "https://help.opera.com/en/latest/web-preferences/#notifications"
}

export const getPermissionsUpdateLink = function () {
  const { browser, platform } = environment.getEnvironment()

  if (browser.name === "Chrome") {
    if (platform.type === "mobile") {
      return pushNotificationPermissionInstructionUrl.chromeAndroid
    } else {
      return pushNotificationPermissionInstructionUrl.chromeDesktop
    }
  } else if (browser.name === "Firefox") {
    return pushNotificationPermissionInstructionUrl.firefox
  } else if (browser.name === "Opera") {
    return pushNotificationPermissionInstructionUrl.opera
  }
}

export const refreshPushNotificationMessagingToken = async () => {
  try {
    const serviceWorkerRegistration = await navigator.serviceWorker.getRegistration("/")
    // https://github.com/firebase/firebase-js-sdk/issues/2364#issuecomment-590723445
    const token = await getToken(messaging, { serviceWorkerRegistration })

    if (token) {
      await configurationApi.updateFirebaseCloudMessagingRegistrationToken(token)
    }
  } catch (error) {
    // Firefox bug, happens when Delete cookies and site data when Firefox is closed is checked in privacy options
    // https://bugzilla.mozilla.org/show_bug.cgi?id=1413615
    if (environment.isFirefox() && error.name === "SecurityError" && error.message === "The operation is insecure.") {
      return
    }
  }
}

export const deletePushNotificationMessagingToken = () => {
  if (messaging) {
    deleteToken(messaging)
  }
}

export const updatePushNotificationEnabledAnalytics = async (notificationsEnabled) => {
  try {
    const appStateStore = useAppStateStore()
    await deviceApi.updatePushNotificationEnabledAnalytics(notificationsEnabled)
    appStateStore.setLastPushNotificationsAnalyticsUpdate(notificationsEnabled)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Failed to send notification analytics data.")
  }
}

export const requestPermission = async () => {
  const permission = await Notification.requestPermission()
  if (permission === "granted") {
    await initializePushNotifications()
    updatePushNotificationEnabledAnalytics(true)
  } else {
    updatePushNotificationEnabledAnalytics(false)
  }
}
